import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatedSection } from '../components/animated/AnimatedSection';
import { MagneticButton } from '../components/animated/MagneticButton';
import { Users, Target, TrendingUp, Award, Briefcase, Shield, BarChart, Settings } from 'lucide-react';
export function ServicesPage() {
  const navigate = useNavigate();
  const services = [{
    icon: <Target className="w-8 h-8 text-blue-600" data-id="element-1255" />,
    title: 'Consulting',
    description: 'SkillzHQ Consulting helps organisations across Africa become more agile, future-ready, and high-performing.',
    details: ['Organisational Development & Change Management', 'Human Capital & Leadership Advisory', 'Culture Transformation', 'Capability Frameworks & Competency Modelling', 'Strategic Planning & Execution Support'],
    why: 'Because we deliver actionable strategies not shelf documents. We co-create, execute, and embed sustainable systems that accelerate growth.'
  }, {
    icon: <Users className="w-8 h-8 text-green-600" data-id="element-1256" />,
    title: 'Recruitment',
    subtitle: 'Hire Talent That Drives Performance',
    description: "Finding top talent shouldn't be hard. SkillzHQ Recruitment connects you with high-calibre professionals who not only fit your roles but amplify your culture.",
    details: ['Executive Search', 'Mid-Level & Technical Hiring', 'Project-Based Recruitment', 'Competency-Based Assessments'],
    why: 'We combine technology, behavioural science, and deep industry knowledge to bring you the best fast. Your next high-impact hire starts here.'
  }, {
    icon: <Award className="w-8 h-8 text-blue-600" data-id="element-1257" />,
    title: 'Corporate Training Programmes',
    subtitle: 'Develop Human-Centred Leaders. Build Teams. Strengthen Organisations.',
    description: 'SkillzHQ delivers immersive training experiences that transform mindsets and equip teams with the competencies needed to deliver value consistently.',
    details: ['Leadership Development', 'Building Highly Effective Teams', 'Customer Experience Excellence', 'Emotional Intelligence & Mindfulness', 'Sales, Service & Growth Skills', 'Digital Literacy', 'Youth Employability & Power Skills'],
    why: 'Each programme is customised, practical, and designed to deliver measurable ROI.'
  }, {
    icon: <Shield className="w-8 h-8 text-green-600" data-id="element-1258" />,
    title: 'Governance',
    subtitle: 'Build Institutions That Work',
    description: 'We help organisations set up and strengthen governance systems that promote accountability, transparency, and excellence.',
    details: ['Board Set-Up & Governance Frameworks', 'Corporate Policies & Compliance Systems', 'Risk & Performance Governance', 'Board Training & Induction'],
    why: 'SkillzHQ ensures your organisation runs on clarity, integrity, and world-class standards.'
  }, {
    icon: <TrendingUp className="w-8 h-8 text-blue-600" data-id="element-1259" />,
    title: 'Strategy Sessions & Corporate Retreats',
    subtitle: 'Your Strategy properly articulated. Your Team Aligned. Excellent Execution',
    description: 'Our expert-led facilitated Strategy Retreats give leadership teams the clarity they need to drive growth.',
    details: ['Strategic Alignment', 'Visioning & Goal Setting', 'Performance Management Frameworks', 'Execution Roadmaps', 'High-Performance Team Building', 'Design Thinking'],
    why: 'Every session is designed to spark innovation, strengthen cohesion, and set organisations on a winning path.'
  }, {
    icon: <Briefcase className="w-8 h-8 text-green-600" data-id="element-1260" />,
    title: 'HR Outsourcing',
    subtitle: 'Focus on Your Core Business—Let Us Handle Your People Systems',
    description: 'SkillzHQ provides full-spectrum HR Outsourcing that reduces costs, increases efficiency, and ensures compliance.',
    details: ['Payroll Management', 'HR Administration', 'Performance Management Systems', 'Employee Relations Support', 'Leave & Benefits Administration', 'Learning and Development'],
    why: 'We bring world-class HR solutions to organisations of all sizes.'
  }, {
    icon: <BarChart className="w-8 h-8 text-blue-600" data-id="element-1261" />,
    title: 'Research',
    subtitle: 'Insights That Power Decisions',
    description: 'SkillzHQ Research helps organisations make evidence-based decisions by delivering high-quality data, analysis, and insights.',
    details: ['Needs Assessments', 'Social Norms Research', 'Labour Market & Skills Research', 'Organisational Diagnostics', 'Monitoring, Evaluation & Learning (MERL)'],
    why: 'From policy to practice, we ensure your decisions are backed by data.'
  }, {
    icon: <Settings className="w-8 h-8 text-green-600" data-id="element-1262" />,
    title: 'Business Process Outsourcing (BPO)',
    subtitle: 'Streamlined Operations. Productivity Multiplied.',
    description: 'We manage your non-core operations so your team can focus on strategy and innovation.',
    details: ['Customer Service Outsourcing', 'Administrative Support', 'Virtual Assistant Services', 'Digital Operations & Data Management'],
    why: 'Our goal: reduce operational burden, increase efficiency, and boost your bottom line.'
  }];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-1263">
      <main className="flex-grow pt-20" data-id="element-1264">
        {/* Hero Section */}
        <section className="relative py-32 overflow-hidden" data-id="element-1265">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-green-50" data-id="element-1266" />
          <div className="absolute top-20 right-10 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20" data-id="element-1267" />

          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-1268">
            <div className="max-w-4xl mx-auto text-center" data-id="element-1269">
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-8" data-id="element-1270">
                SkillzHQ –{' '}
                <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent" data-id="element-1271">
                  Driving Innovation through People Transformation
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 leading-relaxed mb-8" data-id="element-1272">
                We help companies get the right people, grow them the right way,
                and run efficient systems and strengthen their institutions
                through capacity building. That way, everyone wins – the
                business, the team, and even the community.
              </p>
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1273">
                Here's what we actually do for you:
              </p>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 bg-white" data-id="element-1274">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-1275">
            <div className="max-w-7xl mx-auto space-y-24" data-id="element-1276">
              {services.map((service, index) => <AnimatedSection key={index} data-id="element-1277">
                  <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`} data-id="element-1278">
                    <div className={index % 2 === 1 ? 'lg:order-2' : ''} data-id="element-1279">
                      <div className="flex items-center gap-4 mb-6" data-id="element-1280">
                        <div className="p-3 bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl" data-id="element-1281">
                          {service.icon}
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-1282">
                          {service.title}
                        </h2>
                      </div>

                      {service.subtitle && <h3 className="text-xl font-semibold text-blue-600 mb-4" data-id="element-1283">
                          {service.subtitle}
                        </h3>}

                      <p className="text-gray-700 leading-relaxed mb-6" data-id="element-1284">
                        {service.description}
                      </p>

                      <div className="mb-6" data-id="element-1285">
                        <h4 className="font-bold text-gray-900 mb-3" data-id="element-1286">
                          {service.title === 'Consulting' ? 'What We Do' : service.title === 'Recruitment' ? 'Our Recruitment Solutions' : service.title === 'Corporate Training Programmes' ? 'Signature Training Areas' : service.title === 'Governance' ? 'What We Offer' : service.title === 'Strategy Sessions & Corporate Retreats' ? 'Our Retreats Deliver' : service.title === 'HR Outsourcing' ? 'Our HR Outsourcing Covers' : service.title === 'Research' ? 'Our Research Expertise' : 'Our BPO Solutions'}
                        </h4>
                        <ul className="space-y-2" data-id="element-1287">
                          {service.details.map((detail, idx) => <li key={idx} className="flex items-start text-gray-700" data-id="element-1288">
                              <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0" data-id="element-1289" />
                              {detail}
                            </li>)}
                        </ul>
                      </div>

                      {service.title === 'Consulting' && <div className="bg-blue-50 p-6 rounded-xl border border-blue-100" data-id="element-1290">
                          <h4 className="font-bold text-gray-900 mb-2" data-id="element-1291">
                            Why Clients Choose Us
                          </h4>
                          <p className="text-gray-700" data-id="element-1292">{service.why}</p>
                        </div>}
                      {service.title !== 'Consulting' && <p className="text-gray-700 italic" data-id="element-1293">{service.why}</p>}
                    </div>

                    <div className={index % 2 === 1 ? 'lg:order-1' : ''} data-id="element-1294">
                      <div className="aspect-square rounded-3xl bg-gradient-to-br from-blue-100 to-green-100 p-1" data-id="element-1295">
                        <div className="w-full h-full rounded-3xl bg-white flex items-center justify-center" data-id="element-1296">
                          <div className="scale-150" data-id="element-1297">{service.icon}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>)}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <AnimatedSection className="py-32 bg-gradient-to-br from-blue-600 to-green-600 relative overflow-hidden" data-id="element-1298">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]" data-id="element-1299" />
          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-1300">
            <div className="max-w-4xl mx-auto text-center" data-id="element-1301">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-1302">
                Let's talk
              </h2>
              <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto leading-relaxed" data-id="element-1303">
                Grab a 30-minute call with us. We'll look at where you are, spot
                the couple of things that'll move the needle fastest, and give
                you a simple plan you can run with – whether you hire us or not.
              </p>
              <MagneticButton variant="secondary" size="lg" onClick={() => navigate('/contact')} data-id="element-1304">
                Book a 30-minute call →
              </MagneticButton>
              <p className="text-sm text-blue-100 mt-6" data-id="element-1305">
                (We only open 10 of these slots a week, so if you're ready, grab
                one now.)
              </p>
              <p className="text-lg text-white mt-8" data-id="element-1306">
                Looking forward to chatting,
                <br data-id="element-1307" />
                <span className="font-semibold" data-id="element-1308">The SkillzHQ team</span>
              </p>
            </div>
          </div>
        </AnimatedSection>
      </main>
    </div>;
}